import { useEffect, useState } from "react";

import { formatDistanceToNow } from "date-fns";
import { Clock, X, Leaf } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

interface IdleProject {
  project_id: string;
  project_name: string;
  board_name: string;
  last_activity_at: string;
  days_inactive: number;
}

interface GentleNudgeBannerProps {
  workspaceId: string;
}

/**
 * GentleNudgeBanner - A spoon-friendly nudge system
 * 
 * Design principles:
 * - Shows only ONE nudge at a time (not overwhelming)
 * - Rate limited to max 1 nudge per 24 hours per user
 * - Soft, calming design with gentle colors
 * - Easy to dismiss without guilt
 * - Respects user's energy levels
 */
export const GentleNudgeBanner = ({ workspaceId }: GentleNudgeBannerProps) => {
  const [nudge, setNudge] = useState<IdleProject | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkIfShouldShowNudge();
  }, [workspaceId]);

  const checkIfShouldShowNudge = async () => {
    try {
      // Check last nudge time from localStorage
      const lastNudgeTime = localStorage.getItem("lastNudgeTime");
      const now = Date.now();
      
      // Rate limit: Show max once per 24 hours
      if (lastNudgeTime && now - parseInt(lastNudgeTime) < 24 * 60 * 60 * 1000) {
        return;
      }

      // Fetch idle projects (14+ days for gentler threshold)
      const { data, error } = await supabase.rpc("get_stale_projects", {
        p_workspace_id: workspaceId,
        p_days_threshold: 14,
      });

      if (error) throw error;

      // Get dismissed projects from localStorage
      const dismissedJson = localStorage.getItem("dismissedNudges");
      const dismissed = dismissedJson ? JSON.parse(dismissedJson) : [];

      // Find first non-dismissed project
      const availableProjects = (data || []).filter(
        (p: IdleProject) => !dismissed.includes(p.project_id)
      );

      if (availableProjects.length > 0) {
        setNudge(availableProjects[0]); // Show only ONE
        localStorage.setItem("lastNudgeTime", now.toString());
      }
    } catch (error) {
      console.error("Error checking for nudges:", error);
    }
  };

  const handleDismiss = () => {
    if (!nudge) return;

    // Store dismissed project
    const dismissedJson = localStorage.getItem("dismissedNudges");
    const dismissed = dismissedJson ? JSON.parse(dismissedJson) : [];
    dismissed.push(nudge.project_id);
    localStorage.setItem("dismissedNudges", JSON.stringify(dismissed));

    setIsDismissed(true);
    toast.success("Nudge dismissed. Take care of yourself! 🌿");
  };

  const handleCheckIn = () => {
    if (!nudge) return;
    navigate(`/projects/${nudge.project_id}`);
  };

  if (!nudge || isDismissed) {
    return null;
  }

  return (
    <Alert className="border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/20">
      <div className="flex items-start gap-3">
        <Leaf className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <AlertDescription className="text-sm text-green-900 dark:text-green-100 space-y-2">
            <div>
              <span className="font-medium">Gentle reminder:</span>{" "}
              <span className="font-semibold">{nudge.project_name}</span>
              {" "}might appreciate some attention when you have the energy.
            </div>
            <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-300">
              <Clock className="h-3 w-3" />
              <span>
                Last activity{" "}
                {formatDistanceToNow(new Date(nudge.last_activity_at), {
                  addSuffix: true,
                })}
              </span>
              <span>•</span>
              <span className="italic">No pressure, only if you're up for it</span>
            </div>
          </AlertDescription>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCheckIn}
            className="text-green-700 hover:text-green-900 hover:bg-green-100 dark:text-green-300 dark:hover:text-green-100 dark:hover:bg-green-900/40"
          >
            Check In
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            className="text-green-600 hover:text-green-800 hover:bg-green-100 dark:text-green-400 dark:hover:text-green-200 dark:hover:bg-green-900/40"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Alert>
  );
};
